import { ref } from 'vue'
import type { Ref } from 'vue'
import { consola } from 'consola'
import { reliableRetry } from '../lib/retryUtils'

/**
 * Глобальный флаг для предотвращения множественной инициализации
 */
let globalInitialized = false

/**
 * Composable для инициализации системы платежей
 */
export function usePaymentInitialization() {
  const error = ref<string | null>(null)
  const logger = consola.withTag('payment-initialization')

  /**
   * Инициализирует систему платежей
   */
  async function initialize(
    hasUnpaidOrder: Ref<boolean>,
    restoreFromStorage: () => void,
    fetchUnpaidOrders: () => Promise<void>,
    startExpirationTimer: () => void,
    getDebugInfo: () => any,
  ): Promise<void> {
    logger.info('Initialization started', {
      alreadyInitialized: globalInitialized,
      storeState: getDebugInfo(),
    })

    try {
      // Сначала пытаемся восстановить из localStorage
      restoreFromStorage()
      
      if (!hasUnpaidOrder.value) {
        // Если нет данных в localStorage, делаем запрос с retry
        await reliableRetry(() => fetchUnpaidOrders())
      }

      // Запускаем таймер в любом случае
      startExpirationTimer()

      logger.success('Initialization finished', {
        hasUnpaidOrder: hasUnpaidOrder.value,
        finalStoreState: getDebugInfo(),
      })
    } catch (error_: any) {
      logger.error('Initialization failed', error_)
      error.value = error_.message || 'Ошибка при инициализации'
    }
  }

  /**
   * Инициализирует систему с проверкой глобального флага
   */
  async function initializeWithGlobalCheck(
    hasUnpaidOrder: Ref<boolean>,
    restoreFromStorage: () => void,
    fetchUnpaidOrders: () => Promise<void>,
    startExpirationTimer: () => void,
    getDebugInfo: () => any,
  ): Promise<void> {
    logger.debug('initializeWithGlobalCheck called', {
      alreadyInitialized: globalInitialized,
    })

    if (globalInitialized) {
      logger.info('Initialization skipped: already initialized globally')
      return
    }
    
    globalInitialized = true
    await initialize(hasUnpaidOrder, restoreFromStorage, fetchUnpaidOrders, startExpirationTimer, getDebugInfo)
    logger.debug('initializeWithGlobalCheck completed')
  }

  /**
   * Сброс глобального флага инициализации (для тестирования или перезагрузки)
   */
  function resetGlobalInitialization(): void {
    globalInitialized = false
    logger.info('Global initialization flag reset')
  }

  /**
   * Проверяет, была ли система инициализирована
   */
  function isGloballyInitialized(): boolean {
    return globalInitialized
  }

  return {
    // State
    error,
    
    // Actions
    initialize,
    initializeWithGlobalCheck,
    resetGlobalInitialization,
    isGloballyInitialized,
  }
}